import React, { useState, useEffect } from 'react';
import { fileUrl } from '../utils/fileUrl';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Eye, ArrowRight, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [categoryFilter]);

  const fetchPosts = async () => {
    try {
      let url = `${API}/api/blog/posts?limit=20`;
      if (categoryFilter) {
        url += `&category=${encodeURIComponent(categoryFilter)}`;
      }
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return '1 min';
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-black text-white py-6">
          <div className="max-w-6xl mx-auto px-4">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="/onyx-reserve-logo.png" 
                alt="Onyx Reserve" 
                className="h-16"
              />
            </Link>
            <h1 className="text-3xl font-bold">Blog</h1>
            <p className="text-gray-300 mt-2">Insights on luxury travel, Miami events, and corporate transportation</p>
          </div>
        </header>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-center py-12">Loading posts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO-friendly header */}
      <header className="bg-black text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/" className="inline-block mb-4">
            <img 
              src="/onyx-reserve-logo.png" 
              alt="Onyx Reserve" 
              className="h-16"
            />
          </Link>
          <h1 className="text-4xl font-bold">Onyx Reserve Blog</h1>
          <p className="text-gray-300 mt-2 text-lg">
            Your guide to luxury transportation, Miami events, and premium travel experiences
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <main className="flex-1">
            {categoryFilter && (
              <div className="mb-6 flex items-center gap-2">
                <span className="text-gray-600">Filtered by:</span>
                <Badge variant="secondary" className="text-sm">
                  {categoryFilter}
                </Badge>
                <Link to="/blog" className="text-blue-600 hover:underline text-sm ml-2">
                  Clear filter
                </Link>
              </div>
            )}

            {posts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">No blog posts found.</p>
                  <p className="text-gray-400 text-sm mt-2">Check back soon for new content!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <article key={post.id} className="group">
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="flex flex-col md:flex-row">
                        {post.featured_image && (
                          <div className="md:w-1/3">
                            <img 
                              src={fileUrl(post.featured_image)} 
                              alt={post.title}
                              className="w-full h-48 md:h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className={`p-6 ${post.featured_image ? 'md:w-2/3' : 'w-full'}`}>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                            {post.category && (
                              <Badge variant="outline" className="text-xs">
                                {post.category}
                              </Badge>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {estimateReadTime(post.content)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.views || 0} views
                            </span>
                          </div>
                          
                          <h2 className="text-xl font-bold mb-2 group-hover:text-blue-600 transition-colors">
                            <Link to={`/blog/${post.slug}`}>
                              {post.title}
                            </Link>
                          </h2>
                          
                          <p className="text-gray-600 mb-4 line-clamp-2">
                            {post.excerpt || post.meta_description}
                          </p>
                          
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {post.tags.slice(0, 3).map((tag, idx) => (
                                <span key={idx} className="text-xs text-gray-500 flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <Link 
                            to={`/blog/${post.slug}`}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Read More <ArrowRight className="w-4 h-4 ml-1" />
                          </Link>
                        </CardContent>
                      </div>
                    </Card>
                  </article>
                ))}
              </div>
            )}
          </main>

          {/* Sidebar */}
          <aside className="lg:w-80">
            <Card className="sticky top-4">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map((cat, idx) => (
                    <Link 
                      key={idx}
                      to={`/blog?category=${encodeURIComponent(cat.name)}`}
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-100 transition-colors"
                    >
                      <span className="text-gray-700">{cat.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {cat.count}
                      </Badge>
                    </Link>
                  ))}
                </div>

                <hr className="my-6" />

                <h3 className="font-bold text-lg mb-4">Need a Ride?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Experience Miami in luxury with Onyx Reserve's premium black car service.
                </p>
                <Link to="/register?role=rider">
                  <Button className="w-full bg-gold-500 text-black hover:bg-gold-400">
                    Book Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img 
            src="/onyx-reserve-logo.png" 
            alt="Onyx Reserve" 
            className="h-12 mx-auto mb-4"
          />
          <p className="text-gray-400">
            © {new Date().getFullYear()} Onyx Reserve. Premium Black Car Service in Miami.
          </p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
            <Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link>
            <Link to="/login" className="text-gray-400 hover:text-white">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogList;
